import React, { useEffect, useMemo, useState } from 'react';

import { useDebouncedSearch } from '../../../hooks/useDebouncedSearch';
import ScanBagFallback from './ScanBagFallback';
import ZoomImage from './ZoomImage';
import { deliveryAPI, FeedInfo, ScannedProduct } from '../../../services/deliveryAPI';
import { bridgeClubAPI } from '../../../services/bridgeClubAPI';
import { shouldShowFormType } from './feedConstants';
import '../../../pages/BridgeClub/BridgeClubFeed.css';

// THE TOP GROUP IS RED BRIDGE FEED ONLY (Brad, 2026-09-18). The 47 hand-entered
// manufacturer rows used to sit here, ABOVE the catalog, and picking one never ran
// adoption - so a brand PFX does not carry was a dead end ("we have not set it up
// for delivery yet") and a brand it does carry worked only where someone had mapped
// it by hand. The distributor catalog is now the only way to pick a pet food.
const TOP_GROUP_SOURCE = 'red_bridge';

// feedCategory -> catalog species, so a dog bin is not offered cat food. Categories
// with no catalog species (goat, horse...) search the whole catalog.
const CATALOG_SPECIES: Record<string, string> = { dog: 'dog', cat: 'cat', poultry: 'poultry' };
const isPetCategory = (c: string) => c === 'dog' || c === 'cat';

interface FeedProduct {
  id: number;
  name: string;
  brand: string;
  source: string;
  animal_category: string;
  default_bag_lbs: number;
  bag_sizes: number[];
}

/** The customer's feed choice for one bin. Owned by the parent; the picker only edits it. */
export interface FeedSelection {
  productName: string;
  amount: string;
  formType: string;
  selectedProductId: number | null;
  showOtherInput: boolean;
  // A delivery-catalog Product the customer picked by search or scan, which has
  // no FeedProduct row YET. The dialog adopts it at save - minting one here, on
  // selection, would litter FeedProduct with rows for bags nobody chose.
  //
  // This is what stops a scanned bag becoming an undeliverable bin: the free-
  // text path leaves FeedBin.feed_product null, and store_product_for_bin
  // returns None on a null feed_product.
  catalogProductId: number | null;
}

export const emptyFeedSelection: FeedSelection = {
  productName: '', amount: '', formType: 'pellet', selectedProductId: null,
  showOtherInput: false, catalogProductId: null,
};

/** Valid = a product chosen (listed, catalog, or "other"), an amount, and
 *  (where shown) a form. */
export function isFeedSelectionValid(sel: FeedSelection, feedCategory: string): boolean {
  const requiresForm = shouldShowFormType(feedCategory);
  const chosen = !!sel.selectedProductId || !!sel.catalogProductId || sel.showOtherInput;
  return !!sel.amount && chosen && (!requiresForm || !!sel.formType);
}

interface FeedProductPickerProps {
  feedCategory: string;
  value: FeedSelection;
  onChange: (next: FeedSelection) => void;
  // "Change what we deliver" picks a feed WITHOUT adding any to the bin, so there
  // is no amount to ask for. The picker fills `amount` with the bag size anyway so
  // isFeedSelectionValid still holds.
  hideAmount?: boolean;
  // ONE SCROLLER (Brad, 2026-09-22). Inside a dialog the picker fills the height it is
  // given and only the product list scrolls; the search box and scan link stay put and
  // nothing outside the list needs a scrollbar.
  fillHeight?: boolean;
  // ONLY WHAT COMES IN THE CHOSEN FORM (Brad, 2026-09-22): some rations are pellet
  // only, some mash only, priced differently. With this on, a Red Bridge row is hidden
  // when the server says the chosen form has no bag, and the price shown is that
  // form's. The Feed page leaves it off: a bin may hold feed we do not deliver.
  formFilter?: boolean;
  // THE RECOMMENDED FEED LEADS (Brad, 2026-09-24: "it recommended layer feed. It should
  // be showing that as the leading feed on top, not having to scroll to find layer").
  // Words from the animals (binPlanning.recommendedFeedWords); a Red Bridge row whose
  // name carries one sorts to the top of its group and is tagged. The pick stays the
  // customer's.
  recommended?: string[];
}

// Inline objects, not Tailwind - Tailwind classes are not applied in this build
// and fail silently. The existing className rules here are the component's own
// CSS and stay; these are only the three things the busy state needs.
const PICKER: Record<string, React.CSSProperties> = {
  searchRow: { position: 'relative', display: 'flex', alignItems: 'center' },
  spinner: {
    position: 'absolute', right: '10px', width: '13px', height: '13px',
    border: '2px solid rgba(0,0,0,0.15)', borderTopColor: 'rgba(0,0,0,0.45)',
    borderRadius: '50%', animation: 'fpp-spin 0.6s linear infinite',
    pointerEvents: 'none',
  },
  // Dimmed, and inert while dim - a half-faded row still takes a click, and
  // picking the previous query's bag because it was under the cursor when the
  // list swapped is exactly the mistake this whole change exists to prevent.
  dropdownBusy: { opacity: 0.55, transition: 'opacity 120ms linear',
                  pointerEvents: 'none' },
  // Notes under the "Distributor catalog" label: the prompt, a cut list, a
  // failure. Plain text, quieter than a row, never clickable.
  note: { padding: '8px 12px', fontSize: '13px', color: '#666', lineHeight: 1.4 },
  noteError: { padding: '8px 12px', fontSize: '13px', color: '#b91c1c', lineHeight: 1.4 },
};

// The keyframes have no home in a style object. Injected once, guarded, rather
// than added to a CSS file the component does not otherwise own.
const SPIN_KEYFRAMES = '@keyframes fpp-spin { to { transform: rotate(360deg); } }';
if (typeof document !== 'undefined'
    && !document.getElementById('fpp-spin-keyframes')) {
  const style = document.createElement('style');
  style.id = 'fpp-spin-keyframes';
  style.textContent = SPIN_KEYFRAMES;
  document.head.appendChild(style);
}

/**
 * The branded feed-product picker — the catalog load, the Red-Bridge-first grouping, the
 * auto-filled bag size, the form-type selector. Extracted from AddFeedDialog so the
 * onboarding step and the Feed page share ONE catalog and cannot drift. Controlled: it
 * owns none of the selection state, only edits the FeedSelection passed in.
 */
const ALL_FORMS = ['pellet', 'mash', 'whole'] as const;
type FormType = typeof ALL_FORMS[number];

const FeedProductPicker: React.FC<FeedProductPickerProps> = ({ feedCategory, value, onChange, hideAmount, fillHeight, formFilter, recommended }) => {
  const [feedProducts, setFeedProducts] = useState<FeedProduct[]>([]);
  // TWO FLAGS, NOT ONE. `hasLoaded` says whether we have ever had an answer -
  // it gates the only real empty state. `searching` says a request is in
  // flight, and it must never blank anything: an empty list between keystrokes
  // reads as "no results" for a moment, which is worse than showing results
  // that are one keystroke stale.
  const [hasLoaded, setHasLoaded] = useState(false);
  const [searching, setSearching] = useState(true);
  const [catalogSearching, setCatalogSearching] = useState(false);
  // The instant `productSearch` binds the input so it never blocks; the
  // debounced value is what the SERVER is asked for. Shared hook rather than a
  // local timer, so there is one definition of what debounced search means -
  // DeliveryLeads and Customers use the same one.
  const { value: productSearch, setValue: setProductSearch,
          debounced: appliedSearch, flush: flushSearch } =
    useDebouncedSearch('', 300);
  const [totalCount, setTotalCount] = useState(0);
  // The distributor catalog - 1,060 rows, the same set the scanner reaches.
  // Kept in its own list so the two groups can be labelled honestly rather than
  // merged into one list where the customer cannot tell what is stocked.
  const [catalogHits, setCatalogHits] = useState<ScannedProduct[]>([]);
  // "More about this feed": which catalog rows are expanded to show what is in the
  // bag. A row, not a page - a customer changing feeds reads it where they choose.
  const [openInfo, setOpenInfo] = useState<Set<number>>(new Set());
  // THE PRICE ON A RED BRIDGE ROW is its Treasure Valley bag price, and it depends
  // on the form (pellet or mash) chosen right here - so the info is fetched for the
  // rows on screen and re-fetched when the form changes (Brad, 2026-09-22).
  const [feedInfo, setFeedInfo] = useState<Record<string, FeedInfo>>({});
  const [openFeedInfo, setOpenFeedInfo] = useState<Set<number>>(new Set());
  useEffect(() => {
    let alive = true;
    const ids = feedProducts.map((p) => p.id);
    if (ids.length === 0) { setFeedInfo({}); return; }
    deliveryAPI.feedInfo(ids, value.formType)
      .then((info) => {
        if (!alive) return;
        setFeedInfo(info);
        // A feed that does not come in the chosen form is hidden below; an earlier
        // pick of it must not survive out of sight.
        if (formFilter && value.selectedProductId != null
            && info[value.selectedProductId]?.form_available === false) {
          onChange({ ...value, selectedProductId: null, productName: '', amount: '' });
        }
      })
      // No price is worse shown as a wrong one; a failure leaves the row priceless.
      .catch(() => { if (alive) setFeedInfo({}); });
    return () => { alive = false; };
  }, [feedProducts, value.formType]);
  // THE FORMS ON OFFER (Brad, 2026-09-24: "horse is only a mash. no pellet offered").
  // The buttons are the forms some Red Bridge feed in this category comes in - each
  // row's `forms` lists every form with a priced bag, whichever form was asked - so a
  // category that comes one way shows one button, already chosen, and a customer is
  // never offered a pellet nobody mills. Until the info arrives, or where no Red
  // Bridge feed is listed, all three stay (the list below says what each row lacks).
  const offeredForms = useMemo(() => {
    const found = new Set<string>();
    Object.values(feedInfo).forEach((i) => Object.keys(i?.forms || {}).forEach((f) => found.add(f)));
    return ALL_FORMS.filter((f) => found.has(f));
  }, [feedInfo]);
  useEffect(() => {
    if (!formFilter || offeredForms.length === 0) return;
    if (!offeredForms.includes(value.formType as FormType)) onChange({ ...value, formType: offeredForms[0] });
  }, [offeredForms]);
  // The server's total when the list was cut (SEARCH_LIMIT on the backend). Null
  // means nothing was cut. A truncated list must never look complete.
  const [catalogTotal, setCatalogTotal] = useState<number | null>(null);
  // A failed catalog search is SAID, not swallowed. It used to be caught and
  // shown as an empty list, which a customer reads as "not carried".
  const [catalogError, setCatalogError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      setSearching(true);
      try {
        const params: {
          category?: string; search?: string; page_size?: number; source?: string;
        } = { page_size: 200, source: TOP_GROUP_SOURCE };
        if (feedCategory && feedCategory !== 'other') params.category = feedCategory;
        if (appliedSearch) params.search = appliedSearch;
        const res = await bridgeClubAPI.getFeedProductsDatabase(params);
        // Paginated now: {count, results}. The Array.isArray branch is kept so
        // a stale cached response cannot blank the picker mid-deploy.
        const products = Array.isArray(res) ? res : (res.results || res.products || []);
        if (alive) {
          setFeedProducts(products);
          setTotalCount(Array.isArray(res) ? products.length : (res.count ?? products.length));
        }
      } catch (err) {
        console.error('Failed to load feed products:', err);
        // An ERROR is the one case where stale results must go: we no longer
        // know what matches, and showing the previous query's hits under the
        // current one would be a lie rather than a lag.
        if (alive) { setFeedProducts([]); setTotalCount(0); }
      } finally {
        if (alive) { setSearching(false); setHasLoaded(true); }
      }
    })();
    return () => { alive = false; };
  }, [feedCategory, appliedSearch]);

  // SEARCH MUST REACH WHAT SCAN REACHES. Before this the box searched only the
  // 46 hand-entered FeedProduct rows, so a bag the scanner had just resolved
  // could not be found by typing its name - which reads as a broken search.
  useEffect(() => {
    let alive = true;
    // Under two characters is a DELIBERATE reset, not an in-flight clear -
    // there is no query to be stale about.
    if (appliedSearch.length < 2) {
      setCatalogHits([]); setCatalogTotal(null); setCatalogError(null);
      setCatalogSearching(false); return;
    }
    setCatalogSearching(true);
    deliveryAPI
      .searchCatalog(appliedSearch, CATALOG_SPECIES[feedCategory])
      // OUT-OF-ORDER RESPONSES CANNOT WIN. `alive` is captured per effect run
      // and the cleanup below flips it the moment appliedSearch changes, so a
      // slow response for "nu" landing after a fast one for "nul" has already
      // been disowned by its own closure and cannot reach setState. The guard
      // is the effect identity, not a timestamp - it is exact rather than
      // heuristic, and it covers unmount for free.
      .then((res) => {
        if (!alive) return;
        setCatalogHits(res.results);
        setCatalogTotal(res.truncated ? (res.total ?? null) : null);
        setCatalogError(null);
      })
      // Any member may search now (the endpoint is member-gated, not
      // subscriber-gated). A failure here is a real failure and is shown as
      // one - an empty list would read as "we don't carry it".
      .catch(() => {
        if (!alive) return;
        setCatalogHits([]); setCatalogTotal(null);
        setCatalogError("The catalog search isn't answering right now. Try again in a "
                        + 'moment, or scan the bag.');
      })
      .finally(() => { if (alive) setCatalogSearching(false); });
    return () => { alive = false; };
  }, [appliedSearch, feedCategory]);

  // Either request in flight. Both feed the same list, so one indicator is
  // honest and two would be noise.
  const busy = searching || catalogSearching;

  const handleProductSelect = (productId: number | 'other') => {
    if (productId === 'other') {
      onChange({ ...value, selectedProductId: null, catalogProductId: null,
                 showOtherInput: true, productName: '', amount: '' });
      return;
    }
    const product = feedProducts.find((p) => p.id === productId);
    if (product) {
      onChange({
        ...value,
        selectedProductId: productId,
        // Clear the catalog id - the three sources are mutually exclusive, and
        // a stale one would be adopted at save behind the customer's back.
        catalogProductId: null,
        showOtherInput: false,
        productName: `${product.brand} - ${product.name}`,
        amount: String(product.default_bag_lbs || 40),   // auto-filled bag size
      });
    }
  };

  // Group and sort products: Red Bridge first, then alphabetically by brand.
  //
  // NO CLIENT-SIDE SEARCH FILTER any more - the server does it, across the
  // whole table. Filtering here as well would have re-applied the query to an
  // already-filtered page and silently hidden rows the server had matched,
  // which is the same class of invisible-truncation bug as the old [:200].
  // The position of the first recommended word in a row's name, or -1. Among rows that
  // match, the shorter name is the closer match ("Goat Chow" before "Dairy Goat Chow").
  const recommendedRank = (name: string): number => {
    if (!recommended || recommended.length === 0) return -1;
    const n = (name || '').toLowerCase();
    return recommended.findIndex((w) => n.includes(w));
  };
  const getGroupedProducts = () => {
    const filtered = feedProducts;
    const groups: Record<string, FeedProduct[]> = {};
    filtered.forEach((p) => { (groups[p.brand] = groups[p.brand] || []).push(p); });
    const sortedBrands = Object.keys(groups).sort((a, b) => {
      const aIsRB = a.toLowerCase().includes('red bridge');
      const bIsRB = b.toLowerCase().includes('red bridge');
      if (aIsRB && !bIsRB) return -1;
      if (!aIsRB && bIsRB) return 1;
      return a.localeCompare(b);
    });
    return sortedBrands.map((brand) => ({
      brand,
      products: groups[brand].sort((a, b) => {
        const ra = recommendedRank(a.name);
        const rb = recommendedRank(b.name);
        if (ra !== rb) {
          if (ra === -1) return 1;
          if (rb === -1) return -1;
          return ra - rb;
        }
        if (ra !== -1 && a.name.length !== b.name.length) return a.name.length - b.name.length;
        return a.name.localeCompare(b.name);
      }),
    }));
  };

  const column: React.CSSProperties | undefined = fillHeight
    ? { display: 'flex', flexDirection: 'column', minHeight: 0, flex: '1 1 auto' } : undefined;
  return (
    <div style={fillHeight ? { display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 } : undefined}>
      <div className="form-group" style={column}>
        {!fillHeight && <label>Select Feed Product</label>}
        {/* THE INPUT IS OUTSIDE THE LOADING BRANCH, deliberately. It used to
            live in the else, so every debounced query unmounted the search box
            along with the list - which is not a flicker but a lost text field:
            React swaps element types, the <input> is destroyed and recreated,
            and the caret goes with it. Typing a third character meant typing
            into a box that had just been replaced. */}
        <div style={PICKER.searchRow}>
          <input
            type="text"
            className="product-search"
            value={productSearch}
            onChange={(e) => setProductSearch(e.target.value)}
            // ENTER SEARCHES AND CLOSES THE KEYBOARD (Brad, 2026-09-22): on a phone the
            // keyboard stayed up after "Go" and hid the list. Blurring the box is what
            // dismisses it; enterKeyHint names the key "Search".
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); flushSearch(); e.currentTarget.blur(); } }}
            enterKeyHint="search"
            placeholder="Search products..."
          />
          {busy && <span style={PICKER.spinner} aria-hidden="true" />}
        </div>
        {shouldShowFormType(feedCategory) && (fillHeight || formFilter) && (
          <div style={{ display: 'flex', gap: 6, margin: '6px 0 0', flex: '0 0 auto' }}>
            {(formFilter && offeredForms.length > 0 ? offeredForms : ALL_FORMS).map((f) => (
              <button
                key={f}
                type="button"
                className={`form-type-btn ${value.formType === f ? 'selected' : ''}`}
                style={{ flex: 1, minHeight: 36, padding: '2px 4px', fontSize: 14, lineHeight: 1.2 }}
                onClick={() => onChange({ ...value, formType: f })}
              >
                {f === 'pellet' ? 'Pellet' : f === 'mash' ? 'Mash' : 'Whole grain'}
              </button>
            ))}
          </div>
        )}
      {/* SCAN, RIGHT UNDER THE SEARCH BOX (Brad, 2026-09-22). It used to sit
          below the whole results list as a fallback, where nobody saw it. A
          scanned bag resolves against the same catalog the search box reaches,
          and the customer still confirms the name before anything is saved. */}
      <ScanBagFallback
        onConfirm={(product) => onChange({
          ...value,
          selectedProductId: null,
          // NOT showOtherInput any more. That was the defect: the free-text
          // path saves feed_product_id undefined, which leaves the bin
          // undeliverable. The catalog id is adopted at save instead.
          showOtherInput: false,
          catalogProductId: product.id,
          productName: `${product.brand} - ${product.display_name}`,
          amount: product.weight_lbs ? String(Number(product.weight_lbs)) : value.amount,
        })}
      />
        {!hasLoaded ? (
          <div className="loading-products">Loading products...</div>
        ) : (
          <>
            {/* DIMMED, NOT EMPTIED. The previous results stay on screen while
                the next query is in flight; a list that empties between
                keystrokes reads as "no results" for a moment, and a wrong
                answer that looks confident is worse than a right one that
                looks a beat late. */}
            <div
              className="product-dropdown"
              style={{ ...(busy ? PICKER.dropdownBusy : {}),
                       ...(fillHeight ? { maxHeight: 'none', flex: '1 1 auto', minHeight: 220 } : {}) }}
              aria-busy={busy}
            >
              {getGroupedProducts().map((group) => {
                const inForm = formFilter
                  ? group.products.filter((p) => feedInfo[p.id]?.form_available !== false)
                  : group.products;
                // ONE ROW PER SEASONAL RATION (Brad, 2026-09-22): summer and winter layer
                // are two formulas and one ration. Keep the in-season side, named for
                // the ration; the order brings whichever is in season at lock anyway.
                const rows = formFilter
                  ? inForm.filter((p) => {
                      const key = feedInfo[p.id]?.ration_key;
                      if (!key) return true;
                      const siblings = inForm.filter((q) => feedInfo[q.id]?.ration_key === key);
                      const keep = siblings.find((q) => feedInfo[q.id]?.in_season) || siblings[0];
                      return keep.id === p.id;
                    })
                  : inForm;
                return (
                <div key={group.brand} className="product-group">
                  <div className="product-group-header">
                    {group.brand}
                    {group.brand.toLowerCase().includes('red bridge') && (
                      <span className="rb-badge">★</span>
                    )}
                  </div>
                  {rows.length === 0 && (
                    <div style={PICKER.note}>Nothing here comes as {value.formType}. Try another form.</div>
                  )}
                  {rows.map((product) => (
                    <button
                      key={product.id}
                      className={`product-option ${value.selectedProductId === product.id ? 'selected' : ''}`}
                      style={{ display: 'grid', gridTemplateColumns: feedInfo[product.id]?.image_url ? '56px 1fr' : '1fr', columnGap: 12, alignItems: 'start', padding: '12px 12px' }}
                      onClick={() => handleProductSelect(product.id)}
                    >
                      {feedInfo[product.id]?.image_url && (
                        <ZoomImage src={feedInfo[product.id].image_url} fit="cover" size={56} />
                      )}
                      <span className="product-name" style={{ display: 'block', whiteSpace: 'normal', lineHeight: 1.3, minWidth: 0 }}>
                        <span style={{ fontWeight: 600, fontSize: 16 }}>
                          {formFilter && feedInfo[product.id]?.ration_name ? feedInfo[product.id].ration_name : product.name}
                        </span>
                        {recommendedRank(product.name) !== -1 && (
                          <span style={{ display: 'inline-block', marginLeft: 8, padding: '1px 8px', borderRadius: 999, background: '#dcfce7', color: '#166534', fontSize: 12, fontWeight: 600, verticalAlign: 'middle' }}>
                            Recommended
                          </span>
                        )}
                        {formFilter && feedInfo[product.id]?.seasonal && (
                          <span style={{ display: 'block', fontSize: 13, color: '#888', fontWeight: 400 }}>
                            Summer and winter rations · we bring whichever is in season
                          </span>
                        )}
                        <br />
                        <span className="product-size" style={{ display: 'inline-block', marginTop: 6, fontSize: 14, padding: '2px 8px' }}>
                          {feedInfo[product.id]?.bag_lbs
                            ? `${Number(feedInfo[product.id].bag_lbs)} lb`
                            : `${product.default_bag_lbs} lb`}
                          {feedInfo[product.id]?.price ? ` · $${Number(feedInfo[product.id].price).toFixed(2)}` : ''}
                        </span>
                        {(feedInfo[product.id]?.blurb || feedInfo[product.id]?.nutrition_label_url) && (
                          <>
                            {' '}
                            <span
                              role="button"
                              tabIndex={0}
                              onClick={(e) => { e.stopPropagation(); setOpenFeedInfo((prev) => { const n = new Set(prev); n.has(product.id) ? n.delete(product.id) : n.add(product.id); return n; }); }}
                              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.stopPropagation(); setOpenFeedInfo((prev) => { const n = new Set(prev); n.has(product.id) ? n.delete(product.id) : n.add(product.id); return n; }); } }}
                              style={{ display: 'inline-block', marginTop: 6, marginLeft: 8, fontSize: 14, color: '#8B0000', fontWeight: 600, textDecoration: 'underline', minHeight: 32 }}
                            >
                              {openFeedInfo.has(product.id) ? 'Less' : 'More about this feed'}
                            </span>
                            {openFeedInfo.has(product.id) && (
                              <span style={{ display: 'block', marginTop: 8, fontSize: 15, color: '#374151', lineHeight: 1.5, fontWeight: 400 }}>
                                {feedInfo[product.id].blurb && <span style={{ display: 'block' }}>{feedInfo[product.id].blurb}</span>}
                                <span style={{ display: 'block', marginTop: 4 }}>
                                  {feedInfo[product.id].nutrition_label_url && (
                                    <a href={feedInfo[product.id].nutrition_label_url} target="_blank" rel="noreferrer"
                                      onClick={(e) => e.stopPropagation()} style={{ color: '#8B0000', fontWeight: 600, marginRight: 12 }}>
                                      Nutrition label
                                    </a>
                                  )}
                                  {feedInfo[product.id].info_url && (
                                    <a href={feedInfo[product.id].info_url} target="_blank" rel="noreferrer"
                                      onClick={(e) => e.stopPropagation()} style={{ color: '#8B0000', fontWeight: 600 }}>
                                      Read more on redbridgefeed.com
                                    </a>
                                  )}
                                </span>
                              </span>
                            )}
                          </>
                        )}
                      </span>
                    </button>
                  ))}
                </div>
                );
              })}
              {/* THE DISTRIBUTOR CATALOG - the same 1,060 rows the scanner
                  reaches, and the only way to pick a pet food. Selecting one
                  does NOT mint anything; the id rides on the selection and is
                  adopted at save. */}
              {isPetCategory(feedCategory) && appliedSearch.length < 2 && (
                <div className="product-group">
                  <div className="product-group-label">Distributor catalog</div>
                  <div style={PICKER.note}>
                    Type a brand or product name. Over 700 dog and cat foods,
                    ordered in every week.
                  </div>
                </div>
              )}
              {catalogError && appliedSearch.length >= 2 && (
                <div className="product-group">
                  <div className="product-group-label">Distributor catalog</div>
                  <div style={PICKER.noteError}>{catalogError}</div>
                </div>
              )}
              {!catalogError && !busy && appliedSearch.length >= 2
                && catalogHits.length === 0 && isPetCategory(feedCategory) && (
                <div className="product-group">
                  <div className="product-group-label">Distributor catalog</div>
                  <div style={PICKER.note}>
                    No catalog match for &ldquo;{appliedSearch}&rdquo;. Try the brand
                    name on its own, or scan the bag above.
                  </div>
                </div>
              )}
              {catalogHits.length > 0 && (
                <div className="product-group">
                  <div className="product-group-label">Distributor catalog</div>
                  {catalogTotal !== null && (
                    <div style={PICKER.note}>
                      Showing {catalogHits.length} of {catalogTotal}. Add a word to
                      narrow the list.
                    </div>
                  )}
                  {catalogHits.map((product) => (
                    <button
                      key={`cat-${product.id}`}
                      className={`product-option ${value.catalogProductId === product.id ? 'selected' : ''}`}
                      style={{ display: 'grid', gridTemplateColumns: product.image_url ? '56px 1fr' : '1fr', columnGap: 12, alignItems: 'start', padding: '12px 12px' }}
                      onClick={() => onChange({
                        ...value,
                        selectedProductId: null,
                        showOtherInput: false,
                        catalogProductId: product.id,
                        productName: `${product.brand} - ${product.display_name}`,
                        amount: product.weight_lbs
                          ? String(Number(product.weight_lbs)) : value.amount,
                      })}
                    >
                      {product.image_url && (
                        <ZoomImage src={product.image_url} size={56} />
                      )}
                      <span className="product-name" style={{ display: 'block', whiteSpace: 'normal', lineHeight: 1.3, minWidth: 0 }}>
                        {/* THE REAL NAME (Brad, 2026-09-22), from PFX's own portal, with
                            the price file's abbreviation underneath so a scanned or
                            remembered short name still reads. */}
                        {product.full_name
                          ? <><span style={{ fontWeight: 600, fontSize: 16 }}>{product.full_name}</span><br /><span style={{ fontSize: 13, color: '#888' }}>{product.brand} — {product.display_name}</span></>
                          : <span style={{ fontWeight: 600, fontSize: 16 }}>{product.brand} — {product.display_name}</span>}
                        {/* THE PRICE, on the row (Brad, 2026-09-22): a customer changing
                            feeds wants to see it while choosing. unit_retail is the price
                            the resolver charges - MSRP, or Brad's hand price. */}
                        <br />
                        <span className="product-size" style={{ display: 'inline-block', marginTop: 6, fontSize: 14, padding: '2px 8px' }}>
                          {product.weight_lbs ? `${Number(product.weight_lbs)} lb` : ''}
                          {product.unit_retail ? ` · $${Number(product.unit_retail).toFixed(2)}` : ''}
                        </span>
                        {(product.ingredients || (product.attributes && Object.keys(product.attributes).length > 0)) && (
                          <>
                            {' '}
                            <span
                              role="button"
                              tabIndex={0}
                              onClick={(e) => {
                                e.stopPropagation();
                                setOpenInfo((prev) => { const n = new Set(prev); n.has(product.id) ? n.delete(product.id) : n.add(product.id); return n; });
                              }}
                              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.stopPropagation(); setOpenInfo((prev) => { const n = new Set(prev); n.has(product.id) ? n.delete(product.id) : n.add(product.id); return n; }); } }}
                              style={{ display: 'inline-block', marginTop: 6, marginLeft: 8, fontSize: 14, color: '#8B0000', fontWeight: 600, textDecoration: 'underline', minHeight: 32 }}
                            >
                              {openInfo.has(product.id) ? 'Less' : 'More about this feed'}
                            </span>
                            {openInfo.has(product.id) && (
                              <span style={{ display: 'block', marginTop: 8, fontSize: 15, color: '#374151', lineHeight: 1.5, fontWeight: 400 }}>
                                {product.attributes && Object.entries(product.attributes)
                                  .filter(([k]) => k !== 'Brand')
                                  .map(([k, v]) => (
                                    <span key={k} style={{ display: 'block' }}>
                                      <strong>{k.replace(/_/g, ' ')}:</strong> {(v || []).join(', ')}
                                    </span>
                                  ))}
                                {product.ingredients && (
                                  <span style={{ display: 'block', marginTop: 4 }}>
                                    <strong>Ingredients:</strong> {product.ingredients}
                                  </span>
                                )}
                                <span style={{ display: 'block', marginTop: 4, color: '#888' }}>From the distributor's product listing.</span>
                              </span>
                            )}
                          </>
                        )}
                      </span>
                    </button>
                  ))}
                </div>
              )}

              <div className="product-group">
                <button
                  className={`product-option other-option ${value.showOtherInput ? 'selected' : ''}`}
                  onClick={() => handleProductSelect('other')}
                >
                  <span className="product-name">Other / Unlisted Feed</span>
                  <span className="product-size">Not for delivery</span>
                </button>
              </div>
            </div>
          </>
        )}
      </div>


      {value.showOtherInput && (
        <div className="form-group">
          <label>Product Name</label>
          <input
            type="text"
            value={value.productName}
            onChange={(e) => onChange({ ...value, productName: e.target.value })}
            placeholder="e.g., Local Farm Layer Feed"
          />
        </div>
      )}

      {/* ON HAND, NOT BAG SIZE (Brad, 2026-09-25: "we want to know how much feed they
          currently have on hand so we can judge the delivery time for their need...
          otherwise it automatically thinks we have 40 lb and they may only have 5").
          In the setup flow (formFilter) the amount is asked as what is on hand right
          now, and the hint says why and what to put. The Feed page's own add-feed
          dialog is about a bag just bought, and keeps its wording. */}
      {!hideAmount && <div className="form-group">
        <label>{formFilter ? 'How much do you have on hand right now? (lb)' : 'Amount (lbs)'}</label>
        <input
          type="text"
          inputMode="numeric"
          value={value.amount}
          onChange={(e) => onChange({ ...value, amount: e.target.value })}
          onBlur={(e) => {
            const val = parseFloat(e.target.value) || 0;
            onChange({ ...value, amount: val > 0 ? String(val) : '' });
          }}
          placeholder="40"
        />
        {formFilter ? (
          <span className="amount-hint" style={{ color: '#92400e' }}>
            We plan your first delivery from this. A full new bag is the bag size, so leave it.
            Nearly out? Put what's left, even if it's 5.
          </span>
        ) : value.selectedProductId && (
          <span className="amount-hint">Default bag size auto-filled</span>
        )}
      </div>}

      {shouldShowFormType(feedCategory) && !fillHeight && !formFilter && (
        <div className="form-group">
          <label>Feed Form</label>
          <div className="form-type-grid">
            <button
              type="button"
              className={`form-type-btn ${value.formType === 'pellet' ? 'selected' : ''}`}
              onClick={() => onChange({ ...value, formType: 'pellet' })}
            >
              ⚫ Pellet
            </button>
            <button
              type="button"
              className={`form-type-btn ${value.formType === 'mash' ? 'selected' : ''}`}
              onClick={() => onChange({ ...value, formType: 'mash' })}
            >
              🔘 Mash
            </button>
            <button
              type="button"
              className={`form-type-btn ${value.formType === 'whole' ? 'selected' : ''}`}
              onClick={() => onChange({ ...value, formType: 'whole' })}
            >
              🌾 Whole
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default FeedProductPicker;
